/**
 * Legend row computation and layout utilities.
 */

import { LEGEND_SPACING, canvasScale } from './constants';

const LEGEND_RECT_SWATCH_W = LEGEND_SPACING.rectTextX;
const LEGEND_LINE_SWATCH_W = LEGEND_SPACING.lineTextX;
const LEGEND_MARKER_SWATCH_W = LEGEND_SPACING.markerTextX;
const LEGEND_ITEM_GAP = LEGEND_SPACING.itemGap;
const LEGEND_ROW_HEIGHT = LEGEND_SPACING.rowHeight;

/**
 * Compute the SVG Y-translation for a top-positioned legend.
 */
export function legendTopTranslateY(estimatedRows, width, height) {
  const { h } = canvasScale(width, height);
  const offset = Math.round(LEGEND_SPACING.topOffset * h);
  const rowH = Math.round(LEGEND_SPACING.rowHeight * h);
  return -(offset + (estimatedRows - 1) * rowH);
}

/**
 * Compute scaled legend topSpace for passing to computeChartLayout.
 */
export function legendTopSpace(estimatedRows) {
  return LEGEND_SPACING.baseTopSpace + (estimatedRows - 1) * LEGEND_SPACING.rowHeight;
}

/**
 * Pre-compute how many rows a wrapping legend will occupy and the width of
 * each item.  Used before computeChartLayout to determine topSpace.
 */
export function computeLegendRows(items, availableWidth, legendFontSize = 12, markerScale = 1) {
  if (!items || items.length === 0) return { rows: [], rowCount: 0, rowHeight: LEGEND_ROW_HEIGHT };
  const charW = legendFontSize * 0.58;
  const itemWidths = items.map((item) => {
    const baseSwatchW = item.type === 'line' ? LEGEND_LINE_SWATCH_W
      : item.type === 'marker' ? LEGEND_MARKER_SWATCH_W
      : LEGEND_RECT_SWATCH_W;
    const swatchW = Math.round(baseSwatchW * markerScale);
    return swatchW + item.label.length * charW + LEGEND_ITEM_GAP;
  });

  const rows = [];
  let currentRow = [];
  let currentRowWidth = 0;
  itemWidths.forEach((w, i) => {
    if (currentRow.length > 0 && currentRowWidth + w > availableWidth) {
      rows.push({ items: currentRow, totalWidth: currentRowWidth });
      currentRow = [];
      currentRowWidth = 0;
    }
    currentRow.push(i);
    currentRowWidth += w;
  });
  if (currentRow.length > 0) rows.push({ items: currentRow, totalWidth: currentRowWidth });

  return { rows, rowCount: rows.length, rowHeight: LEGEND_ROW_HEIGHT, itemWidths };
}

/**
 * Compute legend item layout to prevent overflow.
 * @deprecated Use computeLegendRows for wrapping layout instead.
 */
export function computeLegendLayout(itemCount, availableWidth, itemMinWidth = 80) {
  if (itemCount === 0) return { itemWidth: itemMinWidth, maxChars: 15 };

  const naturalWidth = itemCount * itemMinWidth;
  if (naturalWidth <= availableWidth) {
    return { itemWidth: itemMinWidth, maxChars: 15 };
  }

  const fittedWidth = Math.max(60, Math.floor(availableWidth / itemCount));
  const maxChars = Math.max(4, Math.floor((fittedWidth - 22) / (12 * 0.6)));
  return { itemWidth: fittedWidth, maxChars };
}
